// Locale-aware vehicle units. API/cache values stay in their original form;
// these helpers format structured numbers and parse units embedded in raw MNT
// or Soov strings at render time.

import type { LocaleCode } from '../i18n/resolve';

export type VehicleUnit =
  | 'kilometer'
  | 'kilogram'
  | 'millimeter'
  | 'kilowatt'
  | 'cubicCentimeter'
  | 'liter'
  | 'kilometerPerHour'
  | 'literPer100Kilometers'
  | 'gramPerKilometer'
  | 'decibelA'
  | 'revolutionPerMinute'
  | 'kilometerPerYear'
  | 'second';

type UnitLabels = Record<LocaleCode, string>;

const UNIT_LABELS: Record<VehicleUnit, UnitLabels> = {
  kilometer: { en: 'km', et: 'km', ru: 'км', lv: 'km', zh: '公里', th: 'กม.' },
  kilogram: { en: 'kg', et: 'kg', ru: 'кг', lv: 'kg', zh: '千克', th: 'กก.' },
  millimeter: { en: 'mm', et: 'mm', ru: 'мм', lv: 'mm', zh: '毫米', th: 'มม.' },
  kilowatt: { en: 'kW', et: 'kW', ru: 'кВт', lv: 'kW', zh: '千瓦', th: 'กิโลวัตต์' },
  cubicCentimeter: { en: 'cc', et: 'cm³', ru: 'см³', lv: 'cm³', zh: '立方厘米', th: 'ซีซี' },
  liter: { en: 'L', et: 'l', ru: 'л', lv: 'l', zh: '升', th: 'ลิตร' },
  kilometerPerHour: {
    en: 'km/h',
    et: 'km/h',
    ru: 'км/ч',
    lv: 'km/h',
    zh: '公里/小时',
    th: 'กม./ชม.',
  },
  literPer100Kilometers: {
    en: 'L/100 km',
    et: 'l/100 km',
    ru: 'л/100 км',
    lv: 'l/100 km',
    zh: '升/100公里',
    th: 'ลิตร/100 กม.',
  },
  gramPerKilometer: {
    en: 'g/km',
    et: 'g/km',
    ru: 'г/км',
    lv: 'g/km',
    zh: '克/公里',
    th: 'กรัม/กม.',
  },
  decibelA: {
    en: 'dB(A)',
    et: 'dB(A)',
    ru: 'дБ(A)',
    lv: 'dB(A)',
    zh: '分贝(A)',
    th: 'เดซิเบล(A)',
  },
  revolutionPerMinute: {
    en: 'rpm',
    et: 'p/min',
    ru: 'об/мин',
    lv: 'apgr./min',
    zh: '转/分',
    th: 'รอบ/นาที',
  },
  kilometerPerYear: {
    en: 'km/year',
    et: 'km/a',
    ru: 'км/год',
    lv: 'km/gadā',
    zh: '公里/年',
    th: 'กม./ปี',
  },
  second: { en: 's', et: 's', ru: 'с', lv: 's', zh: '秒', th: 'วินาที' },
};

const LOCALE_TAGS: Record<LocaleCode, string> = {
  en: 'en-GB',
  et: 'et-EE',
  ru: 'ru-RU',
  lv: 'lv-LV',
  zh: 'zh-CN',
  th: 'th-TH',
};

function supportedLocale(locale: string): LocaleCode {
  return locale === 'et' || locale === 'ru' || locale === 'lv' || locale === 'zh' || locale === 'th'
    ? locale
    : 'en';
}

export function unitLabel(unit: VehicleUnit, locale: string): string {
  return UNIT_LABELS[unit][supportedLocale(locale)];
}

export function formatLocalizedNumber(
  value: number | null | undefined,
  locale = 'et',
  options: { minimumFractionDigits?: number; maximumFractionDigits?: number } = {},
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  const code = supportedLocale(locale);
  return new Intl.NumberFormat(LOCALE_TAGS[code], {
    maximumFractionDigits: options.maximumFractionDigits ?? 1,
    ...(options.minimumFractionDigits === undefined
      ? {}
      : { minimumFractionDigits: options.minimumFractionDigits }),
  })
    .format(value)
    .replace(/[\u00a0\u202f]/g, ' ');
}

function unitSeparator(locale: string): string {
  return supportedLocale(locale) === 'zh' ? '' : ' ';
}

export function formatLocalizedUnit(
  value: number | null | undefined,
  unit: VehicleUnit,
  locale = 'et',
  options: { minimumFractionDigits?: number; maximumFractionDigits?: number } = {},
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  return `${formatLocalizedNumber(value, locale, options)}${unitSeparator(locale)}${unitLabel(unit, locale)}`;
}

export function formatEuro(value: number | null | undefined, locale = 'et'): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  const code = supportedLocale(locale);
  return new Intl.NumberFormat(LOCALE_TAGS[code], {
    style: 'currency',
    currency: 'EUR',
    currencyDisplay: 'symbol',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
    .format(value)
    .replace(/[\u00a0\u202f]/g, ' ');
}

function parsedNumber(raw: string): { value: number; fractionDigits: number } | null {
  const normalized = raw.replace(/[\s\u00a0\u202f]/g, '').replace(',', '.');
  const value = Number(normalized);
  if (!Number.isFinite(value)) return null;
  const fractionDigits = normalized.includes('.') ? (normalized.split('.')[1]?.length ?? 0) : 0;
  return { value, fractionDigits };
}

function localizedRawUnit(number: string, unit: VehicleUnit, locale: string): string {
  const parsed = parsedNumber(number);
  if (!parsed) return `${number} ${unitLabel(unit, locale)}`;
  return formatLocalizedUnit(parsed.value, unit, locale, {
    minimumFractionDigits: parsed.fractionDigits,
    maximumFractionDigits: Math.max(1, parsed.fractionDigits),
  });
}

/**
 * Translates units embedded in a raw API string. Placeholders protect each
 * replacement so a localized unit (for example Russian "км") is never parsed
 * again by a later rule.
 */
export function localizeUnitText(raw: string | null | undefined, locale = 'et'): string | null {
  const trimmed = raw?.trim();
  if (!trimmed) return null;

  const replacements: string[] = [];
  let value = trimmed;
  const protect = (replacement: string): string => {
    const index = replacements.push(replacement) - 1;
    return `\uE000${index}\uE001`;
  };
  const number = '-?\\d+(?:[\\s\\u00a0\\u202f]\\d{3})*(?:[.,]\\d+)?';

  value = value.replace(
    new RegExp(`(${number})\\s*[-–]\\s*(${number})\\s*s\\b`, 'gi'),
    (_match, low: string, high: string) => {
      const lowParsed = parsedNumber(low);
      const highParsed = parsedNumber(high);
      if (!lowParsed || !highParsed) return _match;
      const lowText = formatLocalizedNumber(lowParsed.value, locale, {
        minimumFractionDigits: lowParsed.fractionDigits,
        maximumFractionDigits: Math.max(1, lowParsed.fractionDigits),
      });
      const highText = formatLocalizedNumber(highParsed.value, locale, {
        minimumFractionDigits: highParsed.fractionDigits,
        maximumFractionDigits: Math.max(1, highParsed.fractionDigits),
      });
      return protect(`${lowText}–${highText}${unitSeparator(locale)}${unitLabel('second', locale)}`);
    },
  );

  const rules: Array<[RegExp, VehicleUnit]> = [
    [new RegExp(`(${number})\\s*l\\s*\\/\\s*100\\s*km\\b`, 'gi'), 'literPer100Kilometers'],
    [new RegExp(`(${number})\\s*km\\s*\\/\\s*(?:year|aasta|a)\\b`, 'gi'), 'kilometerPerYear'],
    [new RegExp(`(${number})\\s*km\\s*\\/\\s*h\\b`, 'gi'), 'kilometerPerHour'],
    [
      new RegExp(
        `(${number})\\s*(?:g\\s*\\/\\s*km\\b|\\(\\s*g\\s*\\/\\s*km\\s*\\))`,
        'gi',
      ),
      'gramPerKilometer',
    ],
    [new RegExp(`(${number})\\s*dB\\s*\\(A\\)`, 'gi'), 'decibelA'],
    [new RegExp(`(${number})\\s*(?:min(?:-1|⁻¹)|rpm)\\b`, 'gi'), 'revolutionPerMinute'],
    [new RegExp(`(${number})\\s*(?:cm\\^?3|cm³|cc)(?![\\p{L}\\p{N}])`, 'giu'), 'cubicCentimeter'],
    [new RegExp(`(${number})\\s*kW\\b`, 'gi'), 'kilowatt'],
    [new RegExp(`(${number})\\s*kg\\b`, 'gi'), 'kilogram'],
    [new RegExp(`(${number})\\s*mm\\b`, 'gi'), 'millimeter'],
    [new RegExp(`(${number})\\s*km\\b`, 'gi'), 'kilometer'],
    [new RegExp(`(${number})\\s*[lL]\\b`, 'g'), 'liter'],
    [new RegExp(`(${number})\\s*s\\b`, 'gi'), 'second'],
  ];

  for (const [pattern, unit] of rules) {
    value = value.replace(pattern, (_match, rawNumber: string) =>
      protect(localizedRawUnit(rawNumber, unit, locale)),
    );
  }

  return value.replace(/\uE000(\d+)\uE001/g, (_match, index: string) => replacements[Number(index)] ?? _match);
}
